import { LectureCarteVitale, VitaleInfos } from 'src/app/utils/interfaces/new/lecture-carte-vitale';
import { InsiRestNir, InsiRestRawVitaleData } from 'src/app/utils/interfaces/new/insi-rest-payloads.interface';

/** Qualité « assuré » (ouvrant droit) d'un bénéficiaire de la carte Vitale */
const VITALE_QUALITY_RIGHT_HOLDER = 0;

/**
 * Position de l'ouvrant droit (assuré) dans le tableau Patients de la lecture de la carte Vitale.
 *
 * @returns -1 si l'ouvrant droit ne figure pas sur la carte
 */
export function findRightHolderIndex(vitaleRead: LectureCarteVitale): number {
  return vitaleRead?.Patients ? vitaleRead.Patients.findIndex(p => p.i_quality === VITALE_QUALITY_RIGHT_HOLDER) : -1;
}

function toInsiRestNir(nir: { s_nir?: string; s_key?: string }): InsiRestNir {
  return {
    s_nir: nir?.s_nir || '',
    s_key: nir?.s_key || ''
  };
}

/**
 * Construit les données brutes attendues par /getInsFromRawVitaleData (WS_INS1, doc §8.2.3)
 * à partir des informations de la carte fournies par le connecteur websocket (hl_getVitaleInfos).
 *
 * Toutes les clés de BeneficiaryVitaleData (§12.1.5) et RightHolderVitaleData (§12.1.22) sont obligatoires
 * pour le proxy : une donnée absente de la carte est envoyée sous forme de chaîne vide.
 *
 * @param beneficiaryInfos Informations du bénéficiaire sélectionné
 * @param rightHolderInfos Informations de l'ouvrant droit. À défaut, le NIR porté par la carte pour le bénéficiaire est utilisé
 * @returns undefined si le connecteur n'a fourni ni les données du bénéficiaire, ni son rang et sa qualité, ni le numéro de série
 */
export function mapVitaleInfosToRawVitaleData(
  beneficiaryInfos: VitaleInfos,
  rightHolderInfos?: VitaleInfos
): InsiRestRawVitaleData | undefined {
  const beneficiary = beneficiaryInfos?.PatientData;
  const serialNumber = beneficiaryInfos?.VitaleData?.s_serialNumber;
  if (!beneficiary || !serialNumber
    || beneficiary.i_rank === undefined || beneficiary.i_rank === null
    || beneficiary.i_qualBenef === undefined || beneficiary.i_qualBenef === null) {
    return undefined;
  }

  const rightHolder = rightHolderInfos?.PatientData;

  return {
    BeneficiaryVitaleData: {
      s_name: beneficiary.s_name || '',
      s_birthName: beneficiary.s_birthName || '',
      s_givenName: beneficiary.s_givenName || '',
      s_birthday: beneficiary.s_birthday || '',
      i_rank: beneficiary.i_rank,
      i_qualBenef: beneficiary.i_qualBenef,
      NirData: {
        Nir: toInsiRestNir(beneficiary.NirData?.Nir),
        CertifiedNir: toInsiRestNir(beneficiary.NirData?.CertifiedNir),
        s_certificationDate: beneficiary.NirData?.s_certificationDate || ''
      },
      HealthCoverage: {
        s_regime: beneficiary.HealthCoverage?.s_regime || '',
        s_desk: beneficiary.HealthCoverage?.s_desk || '',
        s_center: beneficiary.HealthCoverage?.s_center || ''
      }
    },
    RightHolderVitaleData: {
      s_birthName: rightHolder?.s_birthName || '',
      NirData: {
        // Le NIR porté par la carte pour chaque bénéficiaire est celui de l'ouvrant droit
        Nir: toInsiRestNir(rightHolder?.NirData?.Nir || beneficiary.NirData?.Nir)
      }
    },
    s_serialNumber: serialNumber
  };
}
